/* global MutationObserver */
import { panel, refinementList } from 'instantsearch.js/es/widgets'
import { getClosest } from '../../../utils/jsHelpers'
import { titleCase } from '../../../utils/stringHelpers'
import { removeUnknowns } from '../../../utils/search/filters'
import { openModal } from '../../modals/modal'

// Contains some code aimed at V1 views / modal.
// Not using the old openModal from V1 for V2
// Opening V2 modal manually (adding class to show) instead of using our vjs because of Algolia lifecycle

// TODO: Remove later when refactoring out modalOrList
// Tells which facets to avoid rendering a "Show More" / popup
const excludedFacets = [
  'display_price_for_algolia',
  'display_price_trade_user_for_algolia',
  'width_float',
  'sale_percentage_int',
  'height_float',
  'depth_float',
  '_tags',
  'user_slug',
  'age_group_name',
  'free_uk_delivery_for_algolia',
  'free_london_delivery_for_algolia',
  'free_collection',
  'in_time_for_christmas_for_algolia',
  'professionally_reupholstered',
  'reupholstery_available',
  'professionally_refurbished',
  'professionally_cleaned',
  'certificate_of_authenticity',
  'signed_by_artist',
  'sustainable_packaging'
]

const loadedModals = {}

let firstRender = true

function getDesktopPanel (title) {
  return panel({
    templates: {
      header: `<h5>${title}</h5>`
    }
  })
}

function getPanelWithRefinementList (title, attribute) { return getDesktopPanel(title, attribute)(refinementList) }

function createFilterModal (title, facetName) {
  return getPanelWithRefinementList(title, facetName)({
    container: '#filter-modal',
    attribute: facetName,
    sortBy: ['name:asc'],
    operator: 'or',
    searchable: true,
    limit: 100,
    showMore: true,
    // Loads all other filters in one click
    showMoreLimit: 500,
    transformItems (items) {
      return removeUnknowns(items).map(item => ({
        ...item,
        highlighted: titleCase(item.highlighted)
      }))
    }
  })
}

function getTitleForShowMoreLink (showMoreElement) {
  const panel = getClosest(showMoreElement, '.ais-Panel')
  return panel.querySelector('.ais-Panel-header > span > h5').innerText
}

function getFacetForShowMoreLink (showMoreElement) {
  const panel = getClosest(showMoreElement, '.ais-Panel')
  const facetClass = [...panel.classList].find(klass => klass.startsWith('facet-name'))
  return facetClass.replace('facet-name-', '')
}

function getFilterCountForFacet (options, facetName) {
  const facet = options.results.disjunctiveFacets.find(facet => facet.name === facetName)
  if (!facet) return
  return Object.keys(facet.data).length
}

function openWidgetModal (showMoreElement, search, facetName) {
  const title = getTitleForShowMoreLink(showMoreElement)
  const facet = getFacetForShowMoreLink(showMoreElement)
  const modalTitle = document.getElementById('facet-modal-title')
  const modal = document.getElementById('facet-modal')
  const filter = createFilterModal(title, facetName)

  loadedModals[facetName] = filter
  // To know which widget to offload on modal close
  modal.dataset.facet = facet
  modalTitle.textContent = title

  const facetModal = document.getElementById('facet-modal')
  if (facetModal) {
    // TODO: Remove this once we finish the 80% test.
    // We're checking whether we're on V2 to change how we open the modal
    if (facetModal.dataset.designVersion === 'v2') {
      facetModal.classList.add('show')
    } else {
      // We don't use this old code on V2
      openModal(facetModal)
    }
  }

  search.addWidget(filter)
}

function centerFacetPanel (showMoreElement) {
  const filterTitle = getClosest(showMoreElement, '.ais-RefinementList')

  // Adding some space on top of the filter Title when scrolled
  // so it looks nicer and not glued to top of window
  const breathingSpace = 45
  const bodyRect = document.body.getBoundingClientRect().top
  const filterTitleRect = filterTitle.getBoundingClientRect().top
  const filterTitlePosition = filterTitleRect - bodyRect
  const offsetPosition = filterTitlePosition - breathingSpace

  setTimeout(function () {
    window.scrollTo({
      top: offsetPosition,
      behavior: 'auto'
    })
  }, 50)
}

function setShowMoreClickListener (search, facetName, filterCount) {
  const element = document.getElementById('show_more_' + facetName)
  if (!element) return

  element.addEventListener('click', (e) => {
    if (filterCount >= 100) {
      // To prevent InstantSearch normal
      e.preventDefault()
      e.stopPropagation()

      openWidgetModal(e.target, search, facetName)
    } else {
      // Align window with top of extended filter for good access
      centerFacetPanel(e.target)
    }
  })
}

function reinstateShowMoreClickListener (options, search, showMoreElement) {
  const facetName = getFacetForShowMoreLink(showMoreElement)
  const filterCount = getFilterCountForFacet(options, facetName)

  setShowMoreClickListener(search, facetName, filterCount)
}

function setOffloadWidget (search) {
  const facetModal = document.getElementById('facet-modal')
  if (facetModal) {
    facetModal.addEventListener('click', e => {
      if (e.target === facetModal || e.target.classList.contains('close-modal')) {
        const facetName = facetModal.dataset.facet
        const filter = loadedModals[facetName]
        facetModal.dataset.facet = ''

        // We don't have this on V2
        const modalLoading = document.getElementById('modal__loading')
        if (modalLoading) modalLoading.classList.remove('hidden')

        // For better feeling of closing modal
        setTimeout(() => search.removeWidget(filter), 100)
      }
    })
  }
}

function elementIsScrolledIntoView (elem) {
  if (!elem) return

  const docViewTop = window.scrollY
  const docViewBottom = docViewTop + window.innerHeight
  const elemTop = elem.getBoundingClientRect().top + window.scrollY

  return ((elemTop <= docViewBottom) && (elemTop >= docViewTop))
}

function loadMoreFiltersOnScroll () {
  const modal = Array.from(document.getElementsByClassName('facet-modal__filter-list'))[0]

  modal.addEventListener('scroll', function (e) {
    const elem = document.querySelector('#filter-modal .ais-RefinementList-showMore')

    if (elementIsScrolledIntoView(elem)) {
      elem.click()
      elem.remove()
    }
  })
}

function setAllShowMoreClickListeners (options, search) {
  const allFacets = options.results.disjunctiveFacets
  const filteredFacets = allFacets.filter(facet => !excludedFacets.includes(facet.name))
  filteredFacets.forEach((facet) => {
    const filterCount = getFilterCountForFacet(options, facet.name)
    setShowMoreClickListener(search, facet.name, filterCount)
  })
}

// We're listening for changes on ais-RefinementList for each facet
// In order to reinstate out own click listeners on show more links when they're added back in
// after a filter search is made (the link is removed by IE during search and it loses click listeners)
function setMutationObserversOnShowMoreLinks (options, search) {
  const observer = new MutationObserver(function (mutations) {
    const lastMutation = mutations[mutations.length - 1]
    if (lastMutation.addedNodes.length > 0 && lastMutation.addedNodes[0].className === 'ais-RefinementList-showMore') {
      reinstateShowMoreClickListener(options, search, lastMutation.addedNodes[0])
    }
  })

  // Observe each facet
  const elements = Array.from(document.getElementsByClassName('ais-RefinementList'))
  for (let i = 0; i < elements.length; i++) {
    observer.observe(elements[i], { attributes: false, childList: true, characterData: false, subtree: true })
  }
}

function connectModalFilterListeners (options, search) {
  if (!firstRender) return
  firstRender = false

  setAllShowMoreClickListeners(options, search)

  setMutationObserversOnShowMoreLinks(options, search)

  setOffloadWidget(search)

  loadMoreFiltersOnScroll()
}

function hideModalLoading () {
  const modal = document.getElementById('filter-modal')
  const modalLoading = document.getElementById('modal__loading')

  // widget on modal just loaded
  if (modal.innerHTML !== '') {
    // We don't have this element on V2
    modalLoading?.classList.add('hidden')
  }
}

export { connectModalFilterListeners, hideModalLoading }
